import type { TFunction } from 'i18next'

/* The cargo package types, as a picker (owner decision F3, 2026-10-09).
 *
 * The package type was a blank text field, so a Russian offer printed whatever
 * was typed — «12 Pallet» on a Russian page, because typed text is never
 * translated (components/i18n.md). The fix is rule (a) of that page: a picker
 * writes fixed CODES, and each reader words the code in its own language.
 *
 * A CLIENT-SIDE narrowing, as paymentOptions.ts is. The server stores
 * `package_type` as free text up to 64 characters (quote/cargo.go) with no
 * CHECK, so a type typed before the picker existed — "Pallet", "Euro pallet
 * 1200", "40'HC" — still reads back and still prints as stored. Nothing here
 * rejects stored data: such a value opens as «Другое…» with its own text.
 *
 * EXACT MATCH, NO CASE FOLDING. "Pallet" with a capital was typed by a person
 * and is not the code; it prints as typed, here and on the offer. No migration
 * normalises old rows.
 *
 * THE EIGHT CODES EXIST TWICE. The offer words them in the offer's language
 * from `offerPackages` (backend/internal/quote/offer.go), and
 * `packageTypes.test.ts` reads that table and fails when a code or a word
 * differs from the catalogue here — screen and paper must say the same thing.
 * Change a code only together with that table, and never for a label.
 */

/** The eight codes, in the picker's order. Stored verbatim. */
export const packageTypeCodes = [
  'pallet',
  'crate',
  'box',
  'drum',
  'bag',
  'big-bag',
  'roll',
  'bale',
] as const

export type PackageTypeCode = (typeof packageTypeCodes)[number]

/** The picker's «Другое…» entry. It only reveals the text field: the field's
 *  text is what is stored, and this marker never leaves the form. */
export const PACKAGE_TYPE_OTHER = '__other__'

export function isPackageTypeCode(value: string): value is PackageTypeCode {
  return packageTypeCodes.some((code) => code === value)
}

/** A stored package type beside its count, as the screen prints it: a code as
 *  its noun in the reader's language, agreeing with the count («12 паллет»,
 *  "12 pallets"); anything else exactly as stored ("4 Euro pallet 1200"). */
export function packageLabel(value: string, count: number, t: TFunction): string {
  if (isPackageTypeCode(value)) {
    // A template on the code union, so every one of the eight keys is checked
    // against the catalogue; packageTypes.test.ts pins the words themselves.
    return `${count} ${t(`quotes.packageTypes.counted.${value}`, { count })}`
  }
  return value === '' ? String(count) : `${count} ${value}`
}

/** The picker's options: «Не указан», the eight codes in the reader's words,
 *  and «Другое…», which reveals the text field. */
export function packageTypeOptions(t: TFunction): { value: string; label: string }[] {
  return [
    { value: '', label: t('quotes.packageTypes.none') },
    ...packageTypeCodes.map((code) => ({
      value: code,
      label: t(`quotes.packageTypes.options.${code}`),
    })),
    { value: PACKAGE_TYPE_OTHER, label: t('quotes.packageTypes.other') },
  ]
}
